import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { AutomataService, RuleInterface, WorkflowStepInterface } from '@vendasta/automata';
import { WorkflowStepOutputParameterContainer } from '@vendasta/automata/lib/_internal/objects';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TRIGGER_STEP_ID } from '../../../../../common/constants';
import { AutomationsFeatureCache } from '@galaxy/automata/shared';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { FilterDefinitionService } from '../../../filters/definitions/filter-definition.service';
import {
  StepFilterComponentInterface,
  WorkflowStepFilterComponent,
} from '../../../filters/workflow-step-filter/workflow-step-filter.component';

@Component({
  selector: 'automata-create-task-panel',
  templateUrl: './if-else-branch-panel.component.html',
  styleUrls: ['./if-else-branch-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    WorkflowStepFilterComponent,
  ],
})
export class IfElseBranchPanelComponent extends CommonActionPanelComponent implements OnInit, OnDestroy, AfterViewInit {
  copyOfStep: WorkflowStepInterface;

  @ViewChildren('workflowStepFilter')
  workflowStepFilterComponent: QueryList<StepFilterComponentInterface>;

  predecessorOutputParams$: Observable<WorkflowStepOutputParameterContainer[]>;

  showInternalFilters$: Observable<boolean> = this.automationsFeatureFlags.includeInternalFeatures$;

  private destroyed$$ = new Subject<void>();

  get taskDefinitionId(): string {
    return this.workflowStepFilterComponent.first.getTaskDefinitionId();
  }

  set taskDefinitionId(taskDefinitionId: string) {
    // This does nothing, just here to fulfill the interface.
  }

  constructor(
    private readonly filterDefinitions: FilterDefinitionService,
    private readonly automationsFeatureFlags: AutomationsFeatureCache,
    private readonly automataService: AutomataService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.predecessorOutputParams$ = this.automataService.getWorkflowStepPredecessorOutputParams(
      this.automation.id,
      (this.precedingSteps || []).concat(TRIGGER_STEP_ID),
      true,
    );
    this.copyOfStep = {
      taskDefinitionId: '',
      choices: {
        rules: [null],
      },
    };
    if (this.step?.taskDefinitionId) {
      this.copyOfStep = JSON.parse(JSON.stringify(this.step));
    }
  }

  ngAfterViewInit(): void {
    // The save button is enabled when filters are not loading.
    this.workflowStepFilterComponent.changes.subscribe((comp: QueryList<StepFilterComponentInterface>) => {
      comp.first
        .isLoading()
        .pipe(takeUntil(this.destroyed$$))
        .subscribe((loading) => (this.savingEnabled = !loading));
    });
  }

  getRules(): RuleInterface[] {
    const filter = this.workflowStepFilterComponent.first.getWorkflowStep();
    const rules = filter.choices?.rules || [];
    return rules;
  }

  getData(): any {
    const filter = this.workflowStepFilterComponent.first.getWorkflowStep();
    return JSON.parse(filter.data || '{}');
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return this.workflowStepFilterComponent.first.getControlsToValidateOnSave();
  }

  isFormValid(): boolean {
    return this.workflowStepFilterComponent.first.isFormValid();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.complete();
  }
}
